/**
 * P1-003 Governance Automation Dashboard
 * 
 * Three-View Dashboard for Governance Visibility:
 * 1. Operational View - What requires attention? Assigned/Overdue items
 * 2. Governance View - Policy triggers, compliance, patterns
 * 3. Executive View - Strategic decisions, risk indicators, trends
 * 
 * Design Principle: Decision Integrity - full auditability of all governance actions
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Shield, AlertTriangle, CheckCircle, Clock, Target, TrendingUp,
  TrendingDown, Users, FileText, BarChart3, PieChart as PieChartIcon,
  RefreshCw, Loader2, ChevronRight, AlertCircle, Eye, Bell,
  Activity, Zap, Award, Scale, Gauge
} from 'lucide-react';
import {
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  AreaChart, Area
} from 'recharts';
import axios from 'axios';
import { toast } from 'sonner';

const API_BASE = process.env.REACT_APP_BACKEND_URL + '/api';
const getAuthHeaders = () => {
  const token = localStorage.getItem('simpliview_token');
  // TENANT ISOLATION: prefer impersonated tenant, fall back to user's tenant
  const tenantId =
    localStorage.getItem('impersonated_tenant_id') ||
    localStorage.getItem('simpliview_user_tenant_id') ||
    localStorage.getItem('simpliview_tenant_id');
  return {
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...(tenantId ? { 'X-Tenant-ID': tenantId } : {})
  };
};

// =============================================================================
// SHARED COMPONENTS
// =============================================================================

const MetricCard = ({ title, value, subtitle, icon: Icon, trend, color = 'blue', testId }) => {
  const colorMap = {
    blue: { bg: 'bg-blue-50', icon: 'text-blue-600', border: 'border-blue-100' },
    green: { bg: 'bg-emerald-50', icon: 'text-emerald-600', border: 'border-emerald-100' },
    amber: { bg: 'bg-amber-50', icon: 'text-amber-600', border: 'border-amber-100' },
    red: { bg: 'bg-red-50', icon: 'text-red-600', border: 'border-red-100' },
    purple: { bg: 'bg-purple-50', icon: 'text-purple-600', border: 'border-purple-100' },
    slate: { bg: 'bg-slate-50', icon: 'text-slate-600', border: 'border-slate-100' }
  };
  const c = colorMap[color] || colorMap.blue;

  return (
    <div 
      className={`bg-white rounded-xl border ${c.border} p-5 shadow-sm hover:shadow-md transition-shadow`}
      data-testid={testId}
    >
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <p className="text-sm font-medium text-slate-500">{title}</p>
          <div className="flex items-baseline gap-2 mt-1">
            <span className="text-2xl font-bold text-slate-900">{value}</span>
            {trend !== undefined && (
              <span className={`text-sm font-medium flex items-center gap-0.5 ${
                trend > 0 ? 'text-emerald-600' : trend < 0 ? 'text-red-600' : 'text-slate-500'
              }`}>
                {trend > 0 ? <TrendingUp className="w-3.5 h-3.5" /> : trend < 0 ? <TrendingDown className="w-3.5 h-3.5" /> : null}
                {trend > 0 ? '+' : ''}{trend}%
              </span>
            )}
          </div>
          {subtitle && <p className="text-xs text-slate-400 mt-1">{subtitle}</p>}
        </div>
        <div className={`p-3 rounded-xl ${c.bg}`}>
          <Icon className={`w-5 h-5 ${c.icon}`} />
        </div>
      </div>
    </div>
  );
};

const CoverageGauge = ({ label, value, target = 95, color = 'blue' }) => {
  const percentage = Math.min(value, 100);
  const isOnTarget = value >= target;
  
  return (
    <div className="flex flex-col items-center">
      <div className="relative w-24 h-24">
        <svg className="transform -rotate-90" viewBox="0 0 36 36">
          <path
            d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
            fill="none"
            stroke="#e5e7eb"
            strokeWidth="3"
          />
          <path
            d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
            fill="none"
            stroke={isOnTarget ? '#10b981' : percentage >= target * 0.8 ? '#f59e0b' : '#ef4444'}
            strokeWidth="3"
            strokeDasharray={`${percentage}, 100`}
          />
        </svg>
        <div className="absolute inset-0 flex items-center justify-center">
          <span className={`text-lg font-bold ${isOnTarget ? 'text-emerald-600' : 'text-slate-700'}`}>
            {value.toFixed(0)}%
          </span>
        </div>
      </div>
      <p className="text-sm font-medium text-slate-600 mt-2 text-center">{label}</p>
      <p className="text-xs text-slate-400">Target: {target}%</p>
    </div>
  );
};

const ActionItem = ({ action, onViewDetails }) => {
  const statusColors = {
    detected: { bg: 'bg-purple-100', text: 'text-purple-700', label: 'Detected' },
    created: { bg: 'bg-blue-100', text: 'text-blue-700', label: 'Created' },
    assigned: { bg: 'bg-amber-100', text: 'text-amber-700', label: 'Assigned' },
    acknowledged: { bg: 'bg-cyan-100', text: 'text-cyan-700', label: 'Acknowledged' },
    in_progress: { bg: 'bg-indigo-100', text: 'text-indigo-700', label: 'In Progress' },
    resolved: { bg: 'bg-emerald-100', text: 'text-emerald-700', label: 'Resolved' },
    closed: { bg: 'bg-slate-100', text: 'text-slate-700', label: 'Closed' }
  };
  
  const priorityColors = {
    critical: 'border-l-red-500',
    high: 'border-l-amber-500',
    medium: 'border-l-blue-500',
    low: 'border-l-slate-400'
  };
  
  const s = statusColors[action.status] || statusColors.detected;
  const p = priorityColors[action.priority] || priorityColors.medium;
  const isOverdue = action.is_overdue;
  
  return (
    <div 
      className={`bg-white rounded-lg border border-l-4 ${p} p-4 hover:shadow-md transition-shadow cursor-pointer ${
        isOverdue ? 'bg-red-50/30' : ''
      }`}
      onClick={() => onViewDetails?.(action)}
      data-testid={`action-${action.action_id}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className={`px-2 py-0.5 rounded text-xs font-medium ${s.bg} ${s.text}`}>
              {s.label}
            </span>
            {isOverdue && (
              <span className="px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-700 flex items-center gap-1">
                <AlertCircle className="w-3 h-3" /> Overdue
              </span>
            )}
          </div>
          <h4 className="font-medium text-slate-900 truncate">{action.title}</h4>
          <p className="text-sm text-slate-500 mt-1">
            {action.policy_name || action.policy_id} • {action.entity_name}
          </p>
        </div>
        <ChevronRight className="w-5 h-5 text-slate-400 flex-shrink-0" />
      </div>
      {action.owner_name && (
        <div className="flex items-center gap-2 mt-3 pt-3 border-t border-slate-100">
          <Users className="w-4 h-4 text-slate-400" />
          <span className="text-sm text-slate-600">{action.owner_name}</span>
          {action.due_date && (
            <>
              <span className="text-slate-300">•</span>
              <Clock className="w-4 h-4 text-slate-400" />
              <span className="text-sm text-slate-600">
                Due: {new Date(action.due_date).toLocaleDateString()}
              </span>
            </>
          )}
        </div>
      )}
    </div>
  );
};

const ViewTab = ({ active, label, icon: Icon, count, onClick }) => (
  <button
    onClick={onClick}
    className={`flex items-center gap-2 px-4 py-3 rounded-lg font-medium transition-all ${
      active 
        ? 'bg-slate-900 text-white shadow-lg' 
        : 'bg-white text-slate-600 hover:bg-slate-50 border border-slate-200'
    }`}
    data-testid={`tab-${label.toLowerCase().replace(/\s+/g, '-')}`}
  >
    <Icon className="w-5 h-5" />
    <span>{label}</span>
    {count !== undefined && (
      <span className={`ml-1 px-2 py-0.5 rounded-full text-xs font-bold ${
        active ? 'bg-white/20 text-white' : 'bg-slate-100 text-slate-600'
      }`}>
        {count}
      </span>
    )}
  </button>
);

// =============================================================================
// OPERATIONAL VIEW
// =============================================================================

const OperationalView = ({ metrics, actions, onRefresh }) => {
  const myActions = actions.filter(a => a.status !== 'closed');
  const overdueActions = actions.filter(a => a.is_overdue);
  const pendingAcknowledge = actions.filter(a => a.status === 'assigned');
  
  return (
    <div className="space-y-6" data-testid="operational-view">
      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <MetricCard
          title="My Open Actions"
          value={myActions.length}
          subtitle="Requires your attention"
          icon={Bell}
          color="blue"
          testId="metric-open-actions"
        />
        <MetricCard
          title="Overdue Actions"
          value={overdueActions.length}
          subtitle="Past due date"
          icon={AlertTriangle}
          color={overdueActions.length > 0 ? 'red' : 'green'}
          testId="metric-overdue"
        />
        <MetricCard
          title="Pending Acknowledgement"
          value={pendingAcknowledge.length}
          subtitle="Awaiting owner response"
          icon={Clock}
          color="amber"
          testId="metric-pending-ack"
        />
        <MetricCard
          title="SLA Compliance"
          value={`${metrics?.sla_compliance_rate?.toFixed(0) || 100}%`}
          subtitle="Actions closed within SLA"
          icon={CheckCircle}
          color={metrics?.sla_compliance_rate >= 90 ? 'green' : 'amber'}
          testId="metric-sla"
        />
      </div>
      
      {/* Action Lists */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Overdue Actions */}
        <div className="bg-white rounded-xl border p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-slate-900 flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-red-500" />
              Overdue Actions
            </h3>
            <span className="text-sm text-slate-500">{overdueActions.length} items</span>
          </div>
          <div className="space-y-3 max-h-80 overflow-y-auto">
            {overdueActions.length === 0 ? (
              <div className="text-center py-8 text-slate-400">
                <CheckCircle className="w-10 h-10 mx-auto mb-2 text-emerald-400" />
                <p>No overdue actions</p>
              </div>
            ) : (
              overdueActions.map(action => (
                <ActionItem key={action.action_id} action={action} />
              ))
            )}
          </div>
        </div>
        
        {/* Pending My Action */}
        <div className="bg-white rounded-xl border p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-slate-900 flex items-center gap-2">
              <Bell className="w-5 h-5 text-blue-500" />
              Pending My Action
            </h3>
            <span className="text-sm text-slate-500">{pendingAcknowledge.length} items</span>
          </div>
          <div className="space-y-3 max-h-80 overflow-y-auto">
            {pendingAcknowledge.length === 0 ? (
              <div className="text-center py-8 text-slate-400">
                <CheckCircle className="w-10 h-10 mx-auto mb-2 text-emerald-400" />
                <p>No pending acknowledgements</p>
              </div>
            ) : (
              pendingAcknowledge.map(action => (
                <ActionItem key={action.action_id} action={action} />
              ))
            )}
          </div>
        </div>
      </div>
      
      {/* All Open Actions */}
      <div className="bg-white rounded-xl border p-5">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold text-slate-900 flex items-center gap-2">
            <Activity className="w-5 h-5 text-indigo-500" />
            All Open Governance Actions
          </h3>
          <button 
            onClick={onRefresh}
            className="text-sm text-slate-500 hover:text-slate-700 flex items-center gap-1"
          >
            <RefreshCw className="w-4 h-4" /> Refresh
          </button>
        </div>
        <div className="space-y-3 max-h-96 overflow-y-auto">
          {myActions.length === 0 ? (
            <div className="text-center py-8 text-slate-400">
              <Shield className="w-10 h-10 mx-auto mb-2" />
              <p>No open governance actions</p>
            </div>
          ) : (
            myActions.map(action => (
              <ActionItem key={action.action_id} action={action} />
            ))
          )}
        </div>
      </div>
    </div>
  );
};

// =============================================================================
// GOVERNANCE VIEW
// =============================================================================

const GovernanceView = ({ metrics, actions, coverage }) => {
  const COLORS = ['#6366f1', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899'];
  
  // Calculate policy trigger distribution
  const policyDistribution = Object.entries(metrics?.by_policy_type || {}).map(([name, count]) => ({
    name: name?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) || 'Unknown',
    value: count
  }));
  
  // Actions by status for bar chart
  const statusData = Object.entries(metrics?.by_status || {}).map(([status, count]) => ({
    status: status?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
    count
  }));
  
  // Repeat offenders - projects with multiple governance actions
  const projectCounts = {};
  actions.forEach(a => {
    const key = a.entity_name || 'Unknown';
    projectCounts[key] = (projectCounts[key] || 0) + 1;
  });
  const repeatOffenders = Object.entries(projectCounts)
    .filter(([_, count]) => count > 1)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 5);
  
  return (
    <div className="space-y-6" data-testid="governance-view">
      {/* Coverage Metrics */}
      <div className="bg-white rounded-xl border p-6">
        <h3 className="font-semibold text-slate-900 mb-6 flex items-center gap-2">
          <Target className="w-5 h-5 text-purple-500" />
          Decision Coverage (Target: 95%)
        </h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
          <CoverageGauge 
            label="Strategic" 
            value={coverage?.strategic || 0} 
            target={95}
          />
          <CoverageGauge 
            label="Governance" 
            value={coverage?.governance || 0} 
            target={95}
          />
          <CoverageGauge 
            label="Operational" 
            value={coverage?.operational || 0} 
            target={50}
          />
          <CoverageGauge 
            label="Overall" 
            value={coverage?.overall || 0} 
            target={80}
          />
        </div>
      </div>
      
      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Policy Triggers Distribution */}
        <div className="bg-white rounded-xl border p-5">
          <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
            <PieChartIcon className="w-5 h-5 text-indigo-500" />
            Policy Triggers Distribution
          </h3>
          <ResponsiveContainer width="100%" height={250}>
            <PieChart>
              <Pie
                data={policyDistribution}
                cx="50%"
                cy="50%"
                innerRadius={60}
                outerRadius={90}
                paddingAngle={2}
                dataKey="value"
                label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                labelLine={false}
              >
                {policyDistribution.map((_, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </div>
        
        {/* Actions by Status */}
        <div className="bg-white rounded-xl border p-5">
          <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
            <BarChart3 className="w-5 h-5 text-emerald-500" />
            Actions by Status
          </h3>
          <ResponsiveContainer width="100%" height={250}>
            <BarChart data={statusData} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis type="number" />
              <YAxis dataKey="status" type="category" width={100} tick={{ fontSize: 12 }} />
              <Tooltip />
              <Bar dataKey="count" fill="#6366f1" radius={[0, 4, 4, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
      
      {/* Repeat Offenders */}
      <div className="bg-white rounded-xl border p-5">
        <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
          <AlertTriangle className="w-5 h-5 text-amber-500" />
          Projects with Repeated Governance Actions
        </h3>
        {repeatOffenders.length === 0 ? (
          <div className="text-center py-8 text-slate-400">
            <CheckCircle className="w-10 h-10 mx-auto mb-2 text-emerald-400" />
            <p>No repeat threshold breaches detected</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-200">
                  <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Project</th>
                  <th className="text-right py-3 px-4 text-sm font-medium text-slate-600">Actions</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Risk Level</th>
                </tr>
              </thead>
              <tbody>
                {repeatOffenders.map(([project, count]) => (
                  <tr key={project} className="border-b border-slate-100 hover:bg-slate-50">
                    <td className="py-3 px-4 text-sm text-slate-900">{project}</td>
                    <td className="py-3 px-4 text-sm text-right font-medium text-slate-700">{count}</td>
                    <td className="py-3 px-4">
                      <span className={`px-2 py-1 rounded text-xs font-medium ${
                        count >= 5 ? 'bg-red-100 text-red-700' :
                        count >= 3 ? 'bg-amber-100 text-amber-700' :
                        'bg-yellow-100 text-yellow-700'
                      }`}>
                        {count >= 5 ? 'High' : count >= 3 ? 'Medium' : 'Watch'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

// =============================================================================
// EXECUTIVE VIEW
// =============================================================================

const ExecutiveView = ({ metrics, actions, coverage, decisions }) => {
  // Calculate key executive metrics
  const criticalActions = actions.filter(a => a.priority === 'critical' && a.status !== 'closed');
  const avgResolutionTime = metrics?.avg_resolution_hours || 0;
  
  // Decision impact analysis (mock trend data for now)
  const trendData = [
    { month: 'Jan', decisions: 12, coverage: 65 },
    { month: 'Feb', decisions: 18, coverage: 72 },
    { month: 'Mar', decisions: 15, coverage: 78 },
    { month: 'Apr', decisions: 22, coverage: 82 },
    { month: 'May', decisions: 28, coverage: 85 },
    { month: 'Jun', decisions: 32, coverage: 88 }
  ];
  
  return (
    <div className="space-y-6" data-testid="executive-view">
      {/* Executive Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <MetricCard
          title="Decision Integrity Score"
          value={`${Math.round((coverage?.overall || 0) * 0.9 + 10)}%`}
          subtitle="Composite governance score"
          icon={Award}
          color="purple"
          testId="metric-integrity-score"
        />
        <MetricCard
          title="Critical Actions"
          value={criticalActions.length}
          subtitle="Requires executive attention"
          icon={AlertTriangle}
          color={criticalActions.length > 0 ? 'red' : 'green'}
          testId="metric-critical"
        />
        <MetricCard
          title="Avg Resolution Time"
          value={`${avgResolutionTime.toFixed(1)}h`}
          subtitle="From detection to closure"
          icon={Clock}
          color="blue"
          testId="metric-resolution-time"
        />
        <MetricCard
          title="Governance Compliance"
          value={`${metrics?.sla_compliance_rate?.toFixed(0) || 100}%`}
          subtitle="SLA adherence rate"
          icon={Scale}
          color={metrics?.sla_compliance_rate >= 90 ? 'green' : 'amber'}
          testId="metric-compliance"
        />
      </div>
      
      {/* Decision Integrity Overview */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-white rounded-xl border p-6">
          <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-emerald-500" />
            Decision Coverage Trend
          </h3>
          <ResponsiveContainer width="100%" height={280}>
            <AreaChart data={trendData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis dataKey="month" tick={{ fontSize: 12 }} />
              <YAxis yAxisId="left" tick={{ fontSize: 12 }} />
              <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 12 }} />
              <Tooltip />
              <Legend />
              <Area 
                yAxisId="left"
                type="monotone" 
                dataKey="decisions" 
                stroke="#6366f1" 
                fill="#6366f1" 
                fillOpacity={0.2}
                name="Decisions Recorded"
              />
              <Line 
                yAxisId="right"
                type="monotone" 
                dataKey="coverage" 
                stroke="#10b981" 
                strokeWidth={2}
                dot={{ fill: '#10b981' }}
                name="Coverage %"
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
        
        {/* Risk Indicators */}
        <div className="bg-white rounded-xl border p-6">
          <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
            <Gauge className="w-5 h-5 text-red-500" />
            Risk Indicators
          </h3>
          <div className="space-y-4">
            <div className="flex items-center justify-between p-3 rounded-lg bg-slate-50">
              <span className="text-sm text-slate-600">Overdue Actions</span>
              <span className={`font-bold ${
                metrics?.overdue_actions > 0 ? 'text-red-600' : 'text-emerald-600'
              }`}>
                {metrics?.overdue_actions || 0}
              </span>
            </div>
            <div className="flex items-center justify-between p-3 rounded-lg bg-slate-50">
              <span className="text-sm text-slate-600">Unassigned Critical</span>
              <span className={`font-bold ${
                criticalActions.filter(a => !a.owner_id).length > 0 ? 'text-red-600' : 'text-emerald-600'
              }`}>
                {criticalActions.filter(a => !a.owner_id).length}
              </span>
            </div>
            <div className="flex items-center justify-between p-3 rounded-lg bg-slate-50">
              <span className="text-sm text-slate-600">Coverage Gap</span>
              <span className={`font-bold ${
                (coverage?.overall || 0) < 80 ? 'text-amber-600' : 'text-emerald-600'
              }`}>
                {(100 - (coverage?.overall || 0)).toFixed(0)}%
              </span>
            </div>
            <div className="flex items-center justify-between p-3 rounded-lg bg-slate-50">
              <span className="text-sm text-slate-600">Policy Violations (7d)</span>
              <span className="font-bold text-slate-700">
                {actions.filter(a => {
                  const created = new Date(a.created_at);
                  const weekAgo = new Date();
                  weekAgo.setDate(weekAgo.getDate() - 7);
                  return created >= weekAgo;
                }).length}
              </span>
            </div>
          </div>
        </div>
      </div>
      
      {/* Critical Actions Requiring Intervention */}
      <div className="bg-white rounded-xl border p-6">
        <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
          <AlertCircle className="w-5 h-5 text-red-500" />
          Actions Requiring Executive Intervention
        </h3>
        {criticalActions.length === 0 ? (
          <div className="text-center py-8 text-slate-400">
            <CheckCircle className="w-10 h-10 mx-auto mb-2 text-emerald-400" />
            <p>No critical actions pending</p>
          </div>
        ) : (
          <div className="space-y-3">
            {criticalActions.slice(0, 5).map(action => (
              <ActionItem key={action.action_id} action={action} />
            ))}
          </div>
        )}
      </div>
      
      {/* Executive Questions Answered */}
      <div className="bg-gradient-to-r from-slate-900 to-slate-800 rounded-xl p-6 text-white">
        <h3 className="font-semibold mb-4 flex items-center gap-2">
          <Eye className="w-5 h-5" />
          Decision Integrity Status
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-white/10 rounded-lg p-4">
            <p className="text-white/70 text-sm">What happened?</p>
            <p className="font-medium mt-1">{metrics?.total_actions || 0} governance actions recorded</p>
          </div>
          <div className="bg-white/10 rounded-lg p-4">
            <p className="text-white/70 text-sm">Who approved?</p>
            <p className="font-medium mt-1">{metrics?.by_status?.closed || 0} decisions formally closed</p>
          </div>
          <div className="bg-white/10 rounded-lg p-4">
            <p className="text-white/70 text-sm">Can it be audited?</p>
            <p className="font-medium mt-1 flex items-center gap-2">
              <CheckCircle className="w-4 h-4 text-emerald-400" />
              100% traceable
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

// =============================================================================
// MAIN COMPONENT
// =============================================================================

const P1003GovernanceDashboard = () => {
  const navigate = useNavigate();
  const [activeView, setActiveView] = useState('operational');
  const [loading, setLoading] = useState(true);
  const [metrics, setMetrics] = useState(null);
  const [actions, setActions] = useState([]);
  const [coverage, setCoverage] = useState(null);
  
  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const headers = getAuthHeaders();
      
      // Fetch all data in parallel
      const [metricsRes, actionsRes, coverageRes] = await Promise.all([
        axios.get(`${API_BASE}/governance/metrics/dashboard`, { headers }),
        axios.get(`${API_BASE}/governance/actions`, { headers }),
        axios.get(`${API_BASE}/governance/metrics/coverage`, { headers })
      ]);
      
      setMetrics(metricsRes.data.summary || metricsRes.data);
      if (metricsRes.data.by_status) {
        setMetrics(prev => ({ ...prev, by_status: metricsRes.data.by_status, by_policy_type: metricsRes.data.by_policy_type }));
      }
      setActions(actionsRes.data.actions || []);
      setCoverage(coverageRes.data.coverage || {});
    } catch (err) {
      console.error('Failed to load governance data:', err);
      toast.error('Failed to load governance dashboard');
    } finally {
      setLoading(false);
    }
  }, []);
  
  useEffect(() => {
    loadData();
  }, [loadData]);
  
  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-indigo-600 mx-auto mb-2" />
          <p className="text-slate-500">Loading Governance Dashboard...</p>
        </div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-slate-50" data-testid="p1003-governance-dashboard">
      {/* Header */}
      <div className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
                <Shield className="w-7 h-7 text-indigo-600" />
                Governance Dashboard
              </h1>
              <p className="text-sm text-slate-500 mt-1">
                P1-003 Decision Integrity & Policy Enforcement
              </p>
            </div>
            <button
              onClick={loadData}
              className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
              data-testid="refresh-dashboard"
            >
              <RefreshCw className="w-4 h-4" />
              Refresh
            </button>
          </div>
          
          {/* View Tabs */}
          <div className="flex gap-3 mt-4 pb-1 overflow-x-auto">
            <ViewTab
              active={activeView === 'operational'}
              label="Operational"
              icon={Activity}
              count={actions.filter(a => a.status !== 'closed').length}
              onClick={() => setActiveView('operational')}
            />
            <ViewTab
              active={activeView === 'governance'}
              label="Governance"
              icon={Scale}
              onClick={() => setActiveView('governance')}
            />
            <ViewTab
              active={activeView === 'executive'}
              label="Executive"
              icon={Award}
              count={actions.filter(a => a.priority === 'critical' && a.status !== 'closed').length}
              onClick={() => setActiveView('executive')}
            />
          </div>
        </div>
      </div>
      
      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {activeView === 'operational' && (
          <OperationalView metrics={metrics} actions={actions} onRefresh={loadData} />
        )}
        {activeView === 'governance' && (
          <GovernanceView metrics={metrics} actions={actions} coverage={coverage} />
        )}
        {activeView === 'executive' && (
          <ExecutiveView metrics={metrics} actions={actions} coverage={coverage} />
        )}
      </div>
    </div>
  );
};

export default P1003GovernanceDashboard;
